import { ReactNode, useState } from 'react';
import DropdownHeader from 'components/Shared/SearchFilter/DropdownHeader';
import ButtonTransparent from 'components/Shared/Buttons/ButtonTransparent';
import {
  useFloating,
  useClick,
  useInteractions,
  useDismiss,
  autoUpdate,
  autoPlacement,
} from '@floating-ui/react';
import classnames from 'classnames';
import { useKey } from 'react-use';
import { colors } from 'components/styles/variables';
import styles from './styles';

interface DropdownContainerProps {
  title: string;
  children?: ReactNode;
  resetsOnClose: boolean;
  onClick?: () => void;
  disabled?: boolean;
}

export default function DropdownContainer({
  title,
  children,
  onClick,
  disabled,
}: DropdownContainerProps): JSX.Element {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  function handleClick(): void {
    if (!disabled) {
      setIsModalOpen(!isModalOpen);
    }
  }

  function handleClose(): void {
    setIsModalOpen(false);
  }

  useKey('Escape', handleClose);

  const { refs, floatingStyles, context } = useFloating({
    open: isModalOpen,
    onOpenChange: setIsModalOpen,
    whileElementsMounted: autoUpdate,
    middleware: [autoPlacement({ allowedPlacements: ['top', 'bottom'] })],
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss]);

  return (
    <div
      data-testid="dropdown-container"
      className={classnames(
        'dropdown-container',
        isModalOpen && !disabled ? 'black-border' : undefined,
        disabled ? 'dropdownDisabled' : undefined
      )}
    >
      <ButtonTransparent
        aria-haspopup="listbox"
        aria-expanded={isModalOpen ? 'true' : undefined}
        onClick={handleClick}
        ref={refs.setReference}
        {...getReferenceProps()}
      >
        <DropdownHeader
          title={title}
          isPointedUp={isModalOpen && !disabled}
          onClick={onClick}
          disabled={disabled}
        />
      </ButtonTransparent>
      {isModalOpen && !disabled && (
        <div
          data-testid="dropdown-menu"
          ref={refs.setFloating}
          style={{
            ...floatingStyles,
            zIndex: 600,
            background: 'white',
            minWidth: '296px',
            borderRadius: '8px',
            border: `1px solid ${colors.G2}`,
            filter: 'drop-shadow(0px 24px 38px rgba(0, 0, 0, 0.2))',
          }}
          {...getFloatingProps()}
        >
          {children &&
            [children].map((child, i) => {
              return (
                <button className="menu-item" onClick={handleClose} key={i}>
                  {child}
                </button>
              );
            })}
        </div>
      )}
      <style jsx>{styles}</style>
    </div>
  );
}
